import { useCallback, useEffect, useId, useMemo } from 'react'
import debounce from 'lodash.debounce'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import usePrevious from 'hooks/usePrevious'
import useRecommendationPlaceIds from './useRecommendationPlaceIds'
import {
  streamTypeahead,
} from 'actions/SearchActions'
import {
  getTypeaheadDropdownStreamData,
  getTypeaheadNaturalLanguageItemStreamData,
  type TypeaheadDropdownStreamData,
} from 'selectors/typeaheadSelectors'

interface UseTypeaheadStreamingOptions {
  enabled?: boolean
  debounceMs?: number
  inputThreshold?: number
  types?: Array<string>
  limit?: number
  isLuxPlusMember?: boolean
  rollout?: Array<string>
}

interface UseTypeaheadStreamingReturn {
  dropdownData: TypeaheadDropdownStreamData | undefined
  naturalLanguageItemData: App.TypeaheadStreamingData | undefined
  cancelDebounce: () => void
}

function countWhitespace(str: string): number {
  return (str.match(/\s/g) ?? []).length
}

export function useTypeaheadStreaming(
  input: string,
  typeaheadId: string,
  options: UseTypeaheadStreamingOptions = {},
): UseTypeaheadStreamingReturn {
  const {
    enabled = true,
    debounceMs = 300,
    inputThreshold = 2,
    types,
    limit,
    isLuxPlusMember,
    rollout,
  } = options

  const dispatch = useAppDispatch()
  const recentPlaceIds = useRecommendationPlaceIds()
  // Generate a unique listener ID for this hook instance to track state listeners.
  // This allows the socket listener system to know when to clean up - when all
  // state listeners for a typeaheadId are removed, socket listeners are cleaned up.
  const listenerId = useId()
  const prevInput = usePrevious(input, '')

  const dropdownData = useAppSelector(
    state => getTypeaheadDropdownStreamData(state, typeaheadId),
  )
  const naturalLanguageItemData = useAppSelector(
    state => getTypeaheadNaturalLanguageItemStreamData(state, typeaheadId),
  )

  // Create debounced version of streamTypeahead action
  const debouncedStreamTypeahead = useMemo(
    () => debounce((currentInput: string) => {
      dispatch(
        streamTypeahead({
          input: currentInput,
          typeaheadId,
          listenerId,
          types,
          limit,
          isLuxPlusMember,
          rollout,
          recentPlaceIds,
        }),
      )
    }, debounceMs),
    [
      debounceMs,
      typeaheadId,
      listenerId,
      types,
      limit,
      isLuxPlusMember,
      rollout,
      recentPlaceIds,
      dispatch,
    ],
  )

  // Stable cancel function that can be called externally
  const cancelDebounce = useCallback(() => {
    debouncedStreamTypeahead.cancel()
  }, [debouncedStreamTypeahead])

  // Effect for input changes
  useEffect(() => {
    if (!enabled || input.length < inputThreshold) {
      debouncedStreamTypeahead.cancel()
      return
    }

    const addedWhitespace = countWhitespace(input) > countWhitespace(prevInput)

    if (addedWhitespace) {
      debouncedStreamTypeahead(input)
      debouncedStreamTypeahead.flush()
    } else {
      debouncedStreamTypeahead(input)
    }

    return () => {
      debouncedStreamTypeahead.cancel()
    }
  }, [input, prevInput, enabled, inputThreshold, debouncedStreamTypeahead, typeaheadId, dispatch])

  return {
    dropdownData,
    naturalLanguageItemData,
    cancelDebounce,
  }
}
